import { supabase } from './supabaseClient';
import { awardXP } from './civicLevelService';

export type WeeklyCardType = 'artist_wisdom' | 'civic_moment' | 'spark_a_thought';

interface CardFlipRecord {
  id: string;
  user_id: string;
  week_id: string;
  card_type: WeeklyCardType;
  flipped_at: string;
}

const ACTION_IDS: Record<WeeklyCardType, string | null> = {
  artist_wisdom: null,
  civic_moment: null,
  spark_a_thought: null,
};

async function getActionId(cardType: WeeklyCardType): Promise<string | null> {
  if (ACTION_IDS[cardType]) {
    return ACTION_IDS[cardType];
  }

  const titleMap: Record<WeeklyCardType, string> = {
    artist_wisdom: 'Flip Artist Wisdom card',
    civic_moment: 'Flip Civic Moment card',
    spark_a_thought: 'Flip Spark a Thought card',
  };

  const { data, error } = await supabase
    .from('micro_actions')
    .select('id')
    .eq('title', titleMap[cardType])
    .maybeSingle();

  if (error || !data) {
    console.error(`Error fetching action ID for ${cardType}:`, error);
    return null;
  }

  ACTION_IDS[cardType] = data.id;
  return data.id;
}

export async function hasFlippedCardThisWeek(
  userId: string,
  weekId: string,
  cardType: WeeklyCardType
): Promise<boolean> {
  const { data, error } = await supabase
    .from('weekly_card_flips')
    .select('id')
    .eq('user_id', userId)
    .eq('week_id', weekId)
    .eq('card_type', cardType)
    .maybeSingle();

  if (error) {
    console.error('Error checking card flip status:', error);
    return false;
  }

  return !!data;
}

export async function trackCardFlipAndAwardXP(
  userId: string,
  weekId: string,
  cardType: WeeklyCardType
): Promise<{ success: boolean; alreadyFlipped: boolean; xpAwarded?: boolean }> {
  try {
    const alreadyFlipped = await hasFlippedCardThisWeek(userId, weekId, cardType);

    if (alreadyFlipped) {
      return { success: true, alreadyFlipped: true };
    }

    const { error: insertError } = await supabase
      .from('weekly_card_flips')
      .insert({
        user_id: userId,
        week_id: weekId,
        card_type: cardType,
      });

    if (insertError) {
      if (insertError.code === '23505') {
        return { success: true, alreadyFlipped: true };
      }
      console.error('Error recording card flip:', insertError);
      return { success: false, alreadyFlipped: false };
    }

    const actionId = await getActionId(cardType);
    if (actionId) {
      const xpResult = await awardXP(userId, actionId);
      return {
        success: true,
        alreadyFlipped: false,
        xpAwarded: !!xpResult
      };
    }

    return { success: true, alreadyFlipped: false, xpAwarded: false };
  } catch (error) {
    console.error('Exception tracking card flip:', error);
    return { success: false, alreadyFlipped: false };
  }
}

export async function getUserWeeklyCardFlips(
  userId: string,
  weekId: string
): Promise<WeeklyCardType[]> {
  const { data, error } = await supabase
    .from('weekly_card_flips')
    .select('card_type')
    .eq('user_id', userId)
    .eq('week_id', weekId);

  if (error) {
    console.error('Error fetching user card flips:', error);
    return [];
  }

  return data.map(record => record.card_type as WeeklyCardType);
}
